"use client";

import { FormEvent, useState } from "react";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Textarea } from "@/components/ui/Textarea";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";

type ReportBugDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

export function ReportBugDialog({
  isOpen,
  onOpenChange,
}: ReportBugDialogProps) {
  const { reportBug: submitBugReport } = useAppContext();
  const { t } = useLanguage();
  const reportBug = t.reportBug;
  const [message, setMessage] = useState("");

  function handleCancel() {
    onOpenChange(false);
    setMessage("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedMessage = message.trim();

    if (!trimmedMessage) {
      return;
    }

    await submitBugReport(trimmedMessage);
    onOpenChange(false);
    setMessage("");
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{reportBug.title}</DialogTitle>
            <DialogDescription>{reportBug.description}</DialogDescription>
          </DialogHeader>

          <Textarea
            aria-label={reportBug.messageLabel}
            placeholder={reportBug.placeholder}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              {reportBug.cancel}
            </Button>
            <Button type="submit" disabled={!message.trim()}>
              {reportBug.submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
